import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'gatsby';
import { colors, fonts, mobile } from '../theme/tokens';
import { Corners } from '../theme/primitives';

const STORAGE_KEY = 'nologaroia_cookie_consent';

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: none; }
`;

const Banner = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 90;
  width: min(440px, calc(100% - 32px));
  background: ${colors.bg};
  color: ${colors.ink};
  border: 1px solid ${colors.divider};
  padding: 22px;
  box-shadow: 0 12px 32px rgba(43, 43, 45, 0.22);
  font-family: ${fonts.body};
  animation: ${slideUp} 0.4s cubic-bezier(.2,.7,.2,1);
  @media (prefers-reduced-motion: reduce) { animation: none; }
  ${mobile} {
    left: 16px;
    right: 16px;
    width: auto;
    bottom: calc(82px + env(safe-area-inset-bottom));
    padding: 18px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  svg { width: 22px; height: 22px; flex: none; }
  h3 { font-size: 22px; text-transform: uppercase; margin: 0; }
`;

const Text = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.neutral800};
  margin: 0 0 18px;
  a { font-weight: 700; }
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const BaseButton = styled.button`
  height: 44px;
  padding: 0 16px;
  border-radius: 0;
  font-family: ${fonts.heading};
  font-weight: 600;
  font-size: 16px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${colors.ink};
  cursor: pointer;
  border: 1px solid ${colors.divider};
  background: transparent;
  transition: background-color 0.15s;
`;

const AcceptButton = styled(BaseButton)`
  background: ${colors.accent};
  border-color: ${colors.accent};
  &:hover { background: ${colors.accent600}; }
  &:active { background: ${colors.accent700}; }
`;

const DeclineButton = styled(BaseButton)`
  &:hover { background: rgba(29, 31, 32, 0.07); }
  &:active { background: rgba(29, 31, 32, 0.14); }
`;

const CookieIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
    <path d="M8.5 8.5v.01" />
    <path d="M16 15.5v.01" />
    <path d="M12 12v.01" />
    <path d="M11 17v.01" />
    <path d="M7 14v.01" />
  </svg>
);

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const consent = window.localStorage.getItem(STORAGE_KEY);
      if (!consent) {
        setVisible(true);
      }
    } catch (e) {
      // localStorage unavailable (private mode) — show banner anyway
      setVisible(true);
    }
  }, []);

  const setConsent = (value) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      // ignore storage errors
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Banner role="region" aria-label="Preferenze cookie">
      <Corners />
      <Header>
        <CookieIcon />
        <h3>Rispettiamo la tua privacy</h3>
      </Header>
      <Text>
        Utilizziamo cookie tecnici necessari al funzionamento del sito e, con il tuo consenso,
        cookie di terze parti. Puoi accettarli o rifiutarli. Per maggiori dettagli leggi la{' '}
        <Link to="/privacy">Privacy Policy</Link>.
      </Text>
      <Actions>
        <DeclineButton onClick={() => setConsent('declined')}>
          Rifiuta
        </DeclineButton>
        <AcceptButton onClick={() => setConsent('accepted')}>
          Accetta
        </AcceptButton>
      </Actions>
    </Banner>
  );
};

export default CookieConsent;
